import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Palette, Type, Layout, Save, RotateCcw } from 'lucide-react';

const TemplateCustomizer = ({ isOpen, onClose, template, onCustomize, currentCustomization = {} }) => {
  const [customization, setCustomization] = useState({
    primaryColor: currentCustomization.primaryColor || '#3B82F6',
    secondaryColor: currentCustomization.secondaryColor || '#1E40AF',
    accentColor: currentCustomization.accentColor || '#F59E0B',
    backgroundColor: currentCustomization.backgroundColor || '#FFFFFF',
    textColor: currentCustomization.textColor || '#111827',
    headingColor: currentCustomization.headingColor || '#1F2937',
    fontFamily: currentCustomization.fontFamily || 'Inter',
    fontSize: currentCustomization.fontSize || 'medium',
    spacing: currentCustomization.spacing || 'normal',
    borderStyle: currentCustomization.borderStyle || 'solid',
    shadow: currentCustomization.shadow || 'medium',
    ...currentCustomization
  });

  const colorPresets = [
    { name: 'Blue Professional', primary: '#3B82F6', secondary: '#1E40AF', accent: '#F59E0B' },
    { name: 'Green Corporate', primary: '#10B981', secondary: '#047857', accent: '#F59E0B' },
    { name: 'Purple Creative', primary: '#8B5CF6', secondary: '#7C3AED', accent: '#F59E0B' },
    { name: 'Red Executive', primary: '#EF4444', secondary: '#DC2626', accent: '#F59E0B' },
    { name: 'Gray Minimal', primary: '#6B7280', secondary: '#374151', accent: '#F59E0B' },
    { name: 'Indigo Modern', primary: '#6366F1', secondary: '#4F46E5', accent: '#F59E0B' },
    { name: 'Teal Professional', primary: '#14B8A6', secondary: '#0D9488', accent: '#F59E0B' },
    { name: 'Orange Dynamic', primary: '#F97316', secondary: '#EA580C', accent: '#3B82F6' }
  ];

  const fontOptions = [
    { name: 'Inter', value: 'Inter', category: 'Sans-serif' },
    { name: 'Roboto', value: 'Roboto', category: 'Sans-serif' },
    { name: 'Open Sans', value: 'Open Sans', category: 'Sans-serif' },
    { name: 'Lato', value: 'Lato', category: 'Sans-serif' },
    { name: 'Poppins', value: 'Poppins', category: 'Sans-serif' },
    { name: 'Source Sans Pro', value: 'Source Sans Pro', category: 'Sans-serif' },
    { name: 'Playfair Display', value: 'Playfair Display', category: 'Serif' },
    { name: 'Merriweather', value: 'Merriweather', category: 'Serif' },
    { name: 'Lora', value: 'Lora', category: 'Serif' },
    { name: 'Crimson Text', value: 'Crimson Text', category: 'Serif' },
    { name: 'Fira Code', value: 'Fira Code', category: 'Monospace' },
    { name: 'JetBrains Mono', value: 'JetBrains Mono', category: 'Monospace' }
  ];

  const fontSizeOptions = [
    { name: 'Small', value: 'small', description: 'Compact, more content' },
    { name: 'Medium', value: 'medium', description: 'Balanced, standard' },
    { name: 'Large', value: 'large', description: 'Easy to read, spacious' }
  ];

  const spacingOptions = [
    { name: 'Compact', value: 'compact', description: 'Tight spacing' },
    { name: 'Normal', value: 'normal', description: 'Standard spacing' },
    { name: 'Relaxed', value: 'relaxed', description: 'More breathing room' }
  ];

  const shadowOptions = [
    { name: 'None', value: 'none', description: 'No shadow' },
    { name: 'Subtle', value: 'subtle', description: 'Light shadow' },
    { name: 'Medium', value: 'medium', description: 'Standard shadow' },
    { name: 'Bold', value: 'bold', description: 'Strong shadow' }
  ];

  const handleColorChange = (colorType, value) => {
    setCustomization(prev => ({
      ...prev,
      [colorType]: value
    }));
  };

  const handlePresetSelect = (preset) => {
    setCustomization(prev => ({
      ...prev,
      primaryColor: preset.primary,
      secondaryColor: preset.secondary,
      accentColor: preset.accent
    }));
  };

  const handleApply = () => {
    onCustomize(customization);
  };

  const handleReset = () => {
    setCustomization({
      primaryColor: '#3B82F6',
      secondaryColor: '#1E40AF',
      accentColor: '#F59E0B',
      backgroundColor: '#FFFFFF',
      textColor: '#111827',
      headingColor: '#1F2937',
      fontFamily: 'Inter',
      fontSize: 'medium',
      spacing: 'normal',
      borderStyle: 'solid',
      shadow: 'medium'
    });
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-auto"
        >
          {/* Header */}
          <div className="sticky top-0 bg-white border-b border-gray-200 p-6 rounded-t-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Palette className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Customize Template</h2>
                  <p className="text-sm text-gray-600">{template?.name || 'Resume Template'}</p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          <div className="p-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Customization Panel */}
              <div className="space-y-6">
                {/* Color Presets */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <Palette className="w-5 h-5 mr-2" />
                    Color Presets
                  </h3>
                  <div className="grid grid-cols-2 gap-3">
                    {colorPresets.map((preset, index) => (
                      <button
                        key={index}
                        onClick={() => handlePresetSelect(preset)}
                        className="p-3 border border-gray-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 transition-colors text-left"
                      >
                        <div className="flex items-center space-x-2 mb-2">
                          <div className="w-4 h-4 rounded-full" style={{ backgroundColor: preset.primary }}></div>
                          <div className="w-4 h-4 rounded-full" style={{ backgroundColor: preset.secondary }}></div>
                          <div className="w-4 h-4 rounded-full" style={{ backgroundColor: preset.accent }}></div>
                        </div>
                        <p className="text-sm font-medium text-gray-900">{preset.name}</p>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Custom Colors */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Custom Colors</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Primary Color</label>
                      <div className="flex items-center space-x-3">
                        <input
                          type="color"
                          value={customization.primaryColor}
                          onChange={(e) => handleColorChange('primaryColor', e.target.value)}
                          className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
                        />
                        <input
                          type="text"
                          value={customization.primaryColor}
                          onChange={(e) => handleColorChange('primaryColor', e.target.value)}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Secondary Color</label>
                      <div className="flex items-center space-x-3">
                        <input
                          type="color"
                          value={customization.secondaryColor}
                          onChange={(e) => handleColorChange('secondaryColor', e.target.value)}
                          className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
                        />
                        <input
                          type="text"
                          value={customization.secondaryColor}
                          onChange={(e) => handleColorChange('secondaryColor', e.target.value)}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Accent Color</label>
                      <div className="flex items-center space-x-3">
                        <input
                          type="color"
                          value={customization.accentColor}
                          onChange={(e) => handleColorChange('accentColor', e.target.value)}
                          className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
                        />
                        <input
                          type="text"
                          value={customization.accentColor}
                          onChange={(e) => handleColorChange('accentColor', e.target.value)}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Background Color</label>
                      <div className="flex items-center space-x-3">
                        <input
                          type="color"
                          value={customization.backgroundColor}
                          onChange={(e) => handleColorChange('backgroundColor', e.target.value)}
                          className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
                        />
                        <input
                          type="text"
                          value={customization.backgroundColor}
                          onChange={(e) => handleColorChange('backgroundColor', e.target.value)}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Typography */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <Type className="w-5 h-5 mr-2" />
                    Typography
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Font Family</label>
                      <select
                        value={customization.fontFamily}
                        onChange={(e) => handleColorChange('fontFamily', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        {fontOptions.map((font) => (
                          <option key={font.value} value={font.value}>
                            {font.name} ({font.category})
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Font Size</label>
                      <div className="space-y-2">
                        {fontSizeOptions.map((option) => (
                          <label key={option.value} className="flex items-center">
                            <input
                              type="radio"
                              name="fontSize"
                              value={option.value}
                              checked={customization.fontSize === option.value}
                              onChange={(e) => handleColorChange('fontSize', e.target.value)}
                              className="mr-3"
                            />
                            <div>
                              <span className="text-sm font-medium text-gray-900">{option.name}</span>
                              <p className="text-xs text-gray-500">{option.description}</p>
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Layout Options */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <Layout className="w-5 h-5 mr-2" />
                    Layout
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Spacing</label>
                      <div className="space-y-2">
                        {spacingOptions.map((option) => (
                          <label key={option.value} className="flex items-center">
                            <input
                              type="radio"
                              name="spacing"
                              value={option.value}
                              checked={customization.spacing === option.value}
                              onChange={(e) => handleColorChange('spacing', e.target.value)}
                              className="mr-3"
                            />
                            <div>
                              <span className="text-sm font-medium text-gray-900">{option.name}</span>
                              <p className="text-xs text-gray-500">{option.description}</p>
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Shadow</label>
                      <div className="space-y-2">
                        {shadowOptions.map((option) => (
                          <label key={option.value} className="flex items-center">
                            <input
                              type="radio"
                              name="shadow"
                              value={option.value}
                              checked={customization.shadow === option.value}
                              onChange={(e) => handleColorChange('shadow', e.target.value)}
                              className="mr-3"
                            />
                            <div>
                              <span className="text-sm font-medium text-gray-900">{option.name}</span>
                              <p className="text-xs text-gray-500">{option.description}</p>
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Preview Panel */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Live Preview</h3>
                <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                  <div 
                    className="bg-white rounded-lg shadow-lg p-6 transform scale-75 origin-top-left"
                    style={{
                      fontFamily: customization.fontFamily,
                      backgroundColor: customization.backgroundColor,
                      color: customization.textColor,
                      boxShadow: customization.shadow === 'none' ? 'none' : 
                                customization.shadow === 'subtle' ? '0 1px 3px rgba(0,0,0,0.1)' :
                                customization.shadow === 'medium' ? '0 4px 6px rgba(0,0,0,0.1)' :
                                '0 10px 15px rgba(0,0,0,0.1)'
                    }}
                  >
                    <div className="text-center mb-6">
                      <h1 
                        className="text-2xl font-bold mb-2"
                        style={{ color: customization.headingColor }}
                      >
                        John Doe
                      </h1>
                      <p 
                        className="text-lg"
                        style={{ color: customization.primaryColor }}
                      >
                        Software Engineer
                      </p>
                      <p className="text-sm text-gray-600 mt-2">
                        john.doe@email.com | (555) 123-4567 | San Francisco, CA
                      </p>
                    </div>
                    
                    <div className="space-y-4">
                      <div>
                        <h2 
                          className="text-lg font-semibold mb-2 border-b-2 pb-1"
                          style={{ 
                            color: customization.headingColor,
                            borderColor: customization.primaryColor
                          }}
                        >
                          Professional Summary
                        </h2>
                        <p className="text-sm">
                          Experienced software engineer with 5+ years of experience in full-stack development...
                        </p>
                      </div>
                      
                      <div>
                        <h2 
                          className="text-lg font-semibold mb-2 border-b-2 pb-1"
                          style={{ 
                            color: customization.headingColor,
                            borderColor: customization.primaryColor
                          }}
                        >
                          Experience
                        </h2>
                        <div className="space-y-2">
                          <div>
                            <h3 className="font-medium">Senior Software Engineer</h3>
                            <p className="text-sm" style={{ color: customization.primaryColor }}>
                              Tech Company | 2020 - Present
                            </p>
                            <p className="text-sm">Led development of web applications...</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-between mt-8 pt-6 border-t border-gray-200">
              <button
                onClick={handleReset}
                className="flex items-center px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                Reset to Default
              </button>
              
              <div className="flex space-x-3">
                <button
                  onClick={onClose}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleApply}
                  className="flex items-center px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Save className="w-4 h-4 mr-2" />
                  Apply Changes
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default TemplateCustomizer;
